import { t } from "@/i18n";
import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export function PaymentSuccess() {
  const [params] = useSearchParams();
  const { refreshUser } = useAuth();
  const [state, setState] = useState("checking");
  const nav = useNavigate();

  useEffect(() => {
    const sid = params.get("session_id");
    if (!sid) return setState("error");
    let tries = 0;
    let timer;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sid}`);
        if (data.payment_status === "paid") {
          await refreshUser();
          return setState("paid");
        }
        if (["expired", "failed"].includes(data.payment_status)) return setState("error");
      } catch (e) { console.warn("Payment status poll failed, retrying", e); }
      if (++tries >= 15) return setState("timeout");
      timer = setTimeout(poll, 2000);
    };
    poll();
    return () => clearTimeout(timer);
  }, [params, refreshUser]);

  return (
    <div className="max-w-xl mx-auto px-4 py-20" data-testid="payment-success-page">
      <div className="card-soft p-10 text-center space-y-5">
        {state === "checking" && (<><Loader2 className="w-14 h-14 mx-auto animate-spin text-[#FF5252]" /><h1 className="text-3xl font-bold">{t("confirming_your_payment")}</h1></>)}
        {state === "paid" && (<>
          <CheckCircle2 className="w-16 h-16 mx-auto text-emerald-500" />
          <h1 className="text-3xl font-bold" data-testid="payment-status-paid">{t("credits_added")}</h1>
          <p className="text-slate-600">{t("a_receipt_is_on_its_way_to_your_inbox")}</p>
          <button className="btn-pop btn-red mx-auto" onClick={() => nav("/create")} data-testid="payment-continue-button">{t("start_creating")}</button>
        </>)}
        {(state === "error" || state === "timeout") && (<>
          <XCircle className="w-16 h-16 mx-auto text-red-500" />
          <h1 className="text-3xl font-bold" data-testid="payment-status-error">{state === "timeout" ? "Still processing" : "Payment not completed"}</h1>
          <p className="text-slate-600">{state === "timeout" ? "Refresh in a minute — your credits will appear once Stripe confirms." : "No charge was made."}</p>
          <Link to="/credits" className="btn-pop btn-white inline-flex" data-testid="payment-back-to-credits">{t("back_to_credits")}</Link>
        </>)}
      </div>
    </div>
  );
}

export function PaymentCancel() {
  return (
    <div className="max-w-xl mx-auto px-4 py-20" data-testid="payment-cancel-page">
      <div className="card-soft p-10 text-center space-y-5">
        <XCircle className="w-16 h-16 mx-auto text-slate-400" />
        <h1 className="text-3xl font-bold">{t("checkout_cancelled")}</h1>
        <p className="text-slate-600">{t("no_worries_nothing_was_charged")}</p>
        <Link to="/credits" className="btn-pop btn-white inline-flex" data-testid="payment-cancel-back">{t("back_to_credits")}</Link>
      </div>
    </div>
  );
}
